// Generic image-carousel CRUD manager (PX-252 / PX-254 / PX-330). The
// login-screen carousel and the paywall carousel are the same surface — a flat
// pool of images with a preview, an Active toggle, drag-reorder, edit + delete,
// plus a MediaDropzone-backed create form. Both back ends expose the identical
// row shape ({id, image_url, is_active, sort_order, created_at, updated_at})
// over the same /admin/<resource> verb set, so per the "two duplicates →
// extract" invariant the table + form + mutation wiring live here once; the two
// pages are thin wrappers that pass an endpoint + perms + copy.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { moveItem } from '@/lib/reorder'
import { MediaDropzone } from '@/components/MediaDropzone'
import {
  Checkbox,
  DeleteButton,
  DragHandle,
  ErrorBanner,
  FormActions,
  NewItemButton,
  Toggle,
  firstApiError,
} from '@/components/FormControls'
import type { Permission } from '@/types/api'

// Structurally shared across login_images + paywall_images.
export interface CarouselImage {
  id: number
  image_url: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

interface CarouselImageCreate {
  image_url: string
  is_active: boolean
  sort_order: number
}

interface CarouselImageUpdate {
  image_url?: string
  is_active?: boolean
  sort_order?: number
}

// What the create/edit form hands back — order is owned by drag now, so the
// form no longer carries a sort_order field.
interface ImageFormValues {
  image_url: string
  is_active: boolean
}

export function MediaCarouselManager({
  title,
  description,
  endpoint,
  queryKey,
  writePerm,
  accept = 'image',
  deleteConfirmText = 'Delete this image? This removes it permanently.',
}: {
  title: string
  description: string
  /** Base path, e.g. '/admin/login-images'. */
  endpoint: string
  /** TanStack query key for this resource's list. */
  queryKey: readonly unknown[]
  writePerm: Permission
  accept?: 'image' | 'media'
  deleteConfirmText?: string
}) {
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, writePerm)
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey,
    queryFn: () => request<CarouselImage[]>(endpoint),
  })

  const patchImage = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CarouselImageUpdate }) =>
      request<CarouselImage>(`${endpoint}/${id}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const createImage = useMutation({
    mutationFn: (body: CarouselImageCreate) =>
      request<CarouselImage>(endpoint, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const deleteImage = useMutation({
    mutationFn: (id: number) => request(`${endpoint}/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const formError = firstApiError(createImage.error)
  // patchImage errors (inline toggle / edit-save / reorder) + delete errors all
  // surface in the one top banner so they never double-render inside the edit
  // form below.
  const rowError = firstApiError(patchImage.error, deleteImage.error)

  // Default the next image's sort_order to one past the current max so it
  // lands at the end of the carousel rather than colliding at 0.
  const nextSortOrder =
    (listQuery.data?.reduce((max, i) => Math.max(max, i.sort_order), -1) ?? -1) + 1

  const rows = [...(listQuery.data ?? [])].sort((a, b) => a.sort_order - b.sort_order)

  // Drag-reorder persistence (PX-330, mirrors the photoshoot variable reorder in
  // PX-313). Optimistically rewrite the cache to the new order, then PATCH
  // sort_order only for the rows that actually moved; either settle invalidates
  // so a refetch confirms success or rolls the optimistic order back.
  const reorder = (sourceId: number, targetId: number) => {
    if (!canWrite) return
    const ordered = moveItem(rows, String(sourceId), String(targetId), (x) => String(x.id))
    if (ordered === rows) return
    const withSort = ordered.map((x, i) => ({ ...x, sort_order: i }))
    const changed = withSort.filter((x) => rows.find((o) => o.id === x.id)?.sort_order !== x.sort_order)
    if (changed.length === 0) return
    queryClient.setQueryData(queryKey, withSort)
    Promise.all(
      changed.map((x) =>
        request(`${endpoint}/${x.id}`, { method: 'PATCH', body: { sort_order: x.sort_order } }),
      ),
    ).then(
      () => queryClient.invalidateQueries({ queryKey }),
      () => queryClient.invalidateQueries({ queryKey }),
    )
  }

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-ink-400">{description}</p>
      </header>

      {rowError && <ErrorBanner error={rowError} />}

      {canWrite && <NewItemButton label="New image" onClick={() => { setEditingId(null); setCreating(true) }} />}

      {creating && (
        <ImageForm
          accept={accept}
          error={formError}
          onSubmit={(values) =>
            createImage.mutate(
              { ...values, sort_order: nextSortOrder },
              { onSuccess: () => setCreating(false) },
            )
          }
          onCancel={() => { createImage.reset(); setCreating(false) }}
          submitting={createImage.isPending}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="w-10 px-4 py-3"><span className="sr-only">Reorder</span></th>
              <th className="px-4 py-3">Preview</th>
              <th className="px-4 py-3">URL</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {!listQuery.isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-400">No images yet.</td></tr>
            )}
            {rows.map((img) =>
              editingId === img.id ? (
                <tr key={img.id} className="border-b border-ink-100 last:border-0">
                  <td colSpan={5} className="px-4 py-3">
                    <ImageForm
                      accept={accept}
                      mode="edit"
                      initialUrl={img.image_url}
                      initialActive={img.is_active}
                      error={null}
                      onSubmit={(values) =>
                        patchImage.mutate({ id: img.id, body: values }, { onSuccess: () => setEditingId(null) })
                      }
                      onCancel={() => { patchImage.reset(); setEditingId(null) }}
                      submitting={patchImage.isPending}
                    />
                  </td>
                </tr>
              ) : (
                <tr
                  key={img.id}
                  className="border-b border-ink-100 last:border-0"
                  onDragOver={canWrite ? (e) => e.preventDefault() : undefined}
                  onDrop={canWrite ? (e) => {
                    e.preventDefault()
                    const sourceId = Number(e.dataTransfer.getData('text/plain'))
                    if (sourceId) reorder(sourceId, img.id)
                  } : undefined}
                >
                  <td className="px-4 py-3 text-center text-lg">
                    {canWrite && <DragHandle id={String(img.id)} />}
                  </td>
                  <td className="px-4 py-3">
                    <img
                      src={img.image_url}
                      alt="preview"
                      className="h-14 w-14 rounded-md border border-ink-100 object-cover"
                    />
                  </td>
                  <td className="max-w-md px-4 py-3">
                    <a
                      href={img.image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate font-mono text-xs text-brand-600 hover:underline"
                      title={img.image_url}
                    >
                      {img.image_url}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={img.is_active}
                      disabled={!canWrite || patchImage.isPending}
                      onChange={(is_active) => patchImage.mutate({ id: img.id, body: { is_active } })}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={patchImage.isPending}
                          onClick={() => { createImage.reset(); setCreating(false); patchImage.reset(); setEditingId(img.id) }}
                          className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <DeleteButton
                          disabled={deleteImage.isPending}
                          confirmText={deleteConfirmText}
                          onConfirm={() => deleteImage.mutate(img.id)}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      {canWrite ? (
        <p className="mt-3 text-xs text-ink-400">Drag the <span className="font-mono">⠿</span> handle to reorder. Edit replaces the image without losing its place.</p>
      ) : (
        <p className="mt-3 text-xs text-ink-400">
          Read-only — editing needs the <code>{writePerm}</code> permission.
        </p>
      )}
    </div>
  )
}

function ImageForm({
  onSubmit,
  onCancel,
  submitting,
  error,
  accept,
  mode = 'create',
  initialUrl = '',
  initialActive = true,
}: {
  onSubmit: (values: ImageFormValues) => void
  onCancel: () => void
  submitting: boolean
  error: APIError | null
  accept: 'image' | 'media'
  mode?: 'create' | 'edit'
  initialUrl?: string
  initialActive?: boolean
}) {
  const [imageUrl, setImageUrl] = useState(initialUrl)
  const [isActive, setIsActive] = useState(initialActive)

  const canSubmit = imageUrl.trim().length > 0 && !submitting

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSubmit) return
        onSubmit({ image_url: imageUrl.trim(), is_active: isActive })
      }}
      className="mb-4 rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
    >
      {error && <ErrorBanner error={error} />}
      <div className="space-y-3">
        <div>
          <span className="mb-1 block text-xs font-medium text-ink-500">Image</span>
          <MediaDropzone value={imageUrl} onChange={setImageUrl} accept={accept} disabled={submitting} />
        </div>
        <Checkbox label="Active" checked={isActive} onChange={setIsActive} />
      </div>
      <FormActions onCancel={onCancel} submitting={submitting} submitLabel={mode === 'edit' ? 'Save' : 'Create'} />
    </form>
  )
}
